import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface EntityLinkProps {
  /** The id to address. Null, zero or negative means it did not survive validation. */
  readonly id: number | null;
  /** Builds the destination from a usable id. Never called with an unusable one. */
  readonly to: (id: number) => string;
  readonly children: ReactNode;
}

/**
 * A name, linked when its id can actually address something and rendered as
 * plain text when it cannot.
 *
 * These endpoints enforce only the fields a page's claims rest on, so a drifted
 * payload leaves an id null — or 0, where the schema is non-nullable and the
 * row is kept rather than dropped. Interpolating that gives `?trigger_id=null`
 * or `?node_definition_id=0`: a link that renders, navigates, and lands on a
 * list matching nothing. That is worse than no link, because it looks like an
 * answer. Showing the name without one says as much as is actually known.
 */
export const EntityLink = ({ id, to, children }: EntityLinkProps) => {
  if (id === null || id <= 0) {
    return <span>{children}</span>;
  }
  return <Link to={to(id)}>{children}</Link>;
};
